import { ADAPTIVE_TEMPO } from '@/config/training';
import type { ChordVerdict } from '@/types/audio';
import { clamp } from '@/audio/dsp/stats';

export interface TempoDecision {
  bpm: number;
  changed: boolean;
  direction: 'up' | 'down' | 'hold';
}

/**
 * Gradual difficulty adjustment.
 *
 * Deliberately slow to react. A tempo that moves after every attempt teaches
 * the player nothing except that the app is twitchy, and it also destroys the
 * per-BPM statistics the comfortable-tempo estimator depends on. So a change
 * needs three things at once: enough samples, a sustained streak, and a
 * cooldown since the last change.
 */
export class AdaptiveTempo {
  private readonly recent: ChordVerdict[] = [];
  private streak = 0;
  private lastChangeAt = Number.NEGATIVE_INFINITY;

  constructor(
    private bpm: number,
    private readonly enabled: boolean,
  ) {}

  get currentBpm(): number {
    return this.bpm;
  }

  /** Feed one evaluated attempt. `now` is in seconds on any monotonic clock. */
  record(verdict: ChordVerdict, now: number): TempoDecision {
    if (verdict !== 'uncertain') {
      this.recent.push(verdict);
      if (this.recent.length > ADAPTIVE_TEMPO.window) this.recent.shift();
      if (verdict === 'correct') this.streak++;
      else this.streak = 0;
    }

    if (!this.enabled) return { bpm: this.bpm, changed: false, direction: 'hold' };
    if (this.recent.length < ADAPTIVE_TEMPO.minSamples) {
      return { bpm: this.bpm, changed: false, direction: 'hold' };
    }
    if (now - this.lastChangeAt < ADAPTIVE_TEMPO.cooldownSec) {
      return { bpm: this.bpm, changed: false, direction: 'hold' };
    }

    const accuracy = this.accuracy();

    if (accuracy >= ADAPTIVE_TEMPO.raiseAccuracy && this.streak >= ADAPTIVE_TEMPO.raiseStreak) {
      const next = clamp(this.bpm + ADAPTIVE_TEMPO.step, ADAPTIVE_TEMPO.minBpm, ADAPTIVE_TEMPO.maxBpm);
      return this.apply(next, now, 'up');
    }

    if (accuracy <= ADAPTIVE_TEMPO.lowerAccuracy) {
      const next = clamp(this.bpm - ADAPTIVE_TEMPO.step, ADAPTIVE_TEMPO.minBpm, ADAPTIVE_TEMPO.maxBpm);
      return this.apply(next, now, 'down');
    }

    return { bpm: this.bpm, changed: false, direction: 'hold' };
  }

  /** Share of recent attempts that were correct or almost correct. */
  accuracy(): number {
    if (this.recent.length === 0) return 0;
    const good = this.recent.filter((v) => v === 'correct' || v === 'almost').length;
    return good / this.recent.length;
  }

  private apply(next: number, now: number, direction: 'up' | 'down'): TempoDecision {
    if (next === this.bpm) return { bpm: this.bpm, changed: false, direction: 'hold' };
    this.bpm = next;
    this.lastChangeAt = now;
    this.streak = 0;
    this.recent.length = 0;
    return { bpm: next, changed: true, direction };
  }
}
